'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { type ReactNode, useEffect, useState } from 'react';
import { SPRING_GENTLE } from './constants';

interface FadeInProps {
  delay?: number;
  direction?: 'up' | 'down' | 'none';
  duration?: number;
  className?: string;
  children: ReactNode;
  whileInView?: boolean;
}

export function FadeIn({
  delay = 0,
  direction = 'up',
  duration,
  className,
  children,
  whileInView = false,
}: FadeInProps) {
  const prefersReducedMotion = useReducedMotion();
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  if (!hasMounted) {
    return <div className={className}>{children}</div>;
  }

  const yOffset = direction === 'up' ? 8 : direction === 'down' ? -8 : 0;
  const instant = prefersReducedMotion;

  const animateProps = whileInView
    ? {
        initial: instant ? false : { opacity: 0, y: yOffset },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.3 },
      }
    : {
        initial: instant ? false : { opacity: 0, y: yOffset },
        animate: { opacity: 1, y: 0 },
      };

  return (
    <motion.div
      {...animateProps}
      transition={
        instant ? { duration: 0 } : duration ? { duration, delay } : { ...SPRING_GENTLE, delay }
      }
      className={className}
    >
      {children}
    </motion.div>
  );
}
